import CloseIcon from "@mui/icons-material/Close";
import {LoadingButton} from "@mui/lab";
import {Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography, useMediaQuery} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import NewStatusPicker from "crqlar-react-common/dist/components/NewStatusPicker";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {ServiceStatus} from "crqlar-react-common/dist/models/service/serviceStatus.model";
import {ServiceService} from "crqlar-react-common/dist/services/service/service.service";
import {useHasGuestAppModule} from "crqlar-react-common/dist/utils/hooks/guestApp/useGuestAppModule";
import {useAllServices} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQueryClient} from "react-query";
import {DialogField} from "./DigitalSigningTemplateCreateDialog";

type Mode = 'CREATE' | 'LINK';

interface Props {
    open: boolean
    sectorId: string
    /** Whose tip service it sets up; none: the sector's default tip service. */
    staff?: {resourceId: string, name: string}
    onClose: () => void
}

const TITLE_ID = 'tip-service-dialog-title';

/**
 * The sector's services that can take the tips: ones guests can't book. A staff member can also share a tip
 * service that already has staff; the default tip service has none, so it is never one of them.
 */
export const tipServiceChoices = (services: Service[], sectorId: string, forStaff: boolean): Service[] => services
    .filter(service => !service.archived && service.status !== ServiceStatus.ACTIVE && !!service.sectorIds?.includes(sectorId))
    .filter(service => !service.tipService || (forStaff && (service.resourceIds ?? []).some(Boolean)))
    .sort((left, right) => (left.name ?? '').localeCompare(right.name ?? ''));

/**
 * Sets up a staff member's own tip service, or the sector's default one, from Who gets the tip: a new service
 * that guests can't book, or an existing one. Saved on the service at once - the template keeps nothing.
 */
const DigitalSigningTipServiceDialog = ({open, sectorId, staff, onClose}: Props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const queryClient = useQueryClient();
    const {hasGuestAppModule} = useHasGuestAppModule();
    const services = useAllServices();
    const choices = tipServiceChoices(services, sectorId, !!staff);

    const [mode, setMode] = useState<Mode>('CREATE');
    const [name, setName] = useState('');
    const [marketCode, setMarketCode] = useState('');
    const [picked, setPicked] = useState<Service | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string>();

    // Each opening starts over, named after whose tips it takes
    useEffect(() => {
        if (!open) return;
        setMode('CREATE');
        setName(staff ? `${t('Tip')} ${staff.name}` : t('Tip'));
        setMarketCode('');
        setPicked(null);
        setError(undefined);
    }, [open, staff?.resourceId]);

    const replacesResources = mode === 'LINK' && !!picked && !picked.tipService && (picked.resourceIds ?? []).some(Boolean);
    const canSave = mode === 'CREATE' ? !!name.trim() : !!picked?.id;

    // The status guests can't book: the service form saves a service without one as Active
    const notBookable = hasGuestAppModule ? ServiceStatus.ACTIVE_NOT_BOOKABLE : ServiceStatus.DRAFT;

    const save = async () => {
        if (!canSave || saving) return;
        setSaving(true);
        setError(undefined);
        try {
            if (mode === 'CREATE') {
                await ServiceService.createService({
                    name: name.trim(),
                    marketCode: marketCode.trim() || undefined,
                    sectorIds: [sectorId],
                    status: notBookable,
                    tipService: true,
                    resourceIds: staff ? [staff.resourceId] : [],
                });
            } else {
                // As stored: an update replaces the whole service
                const service = await ServiceService.getServiceById(picked!.id!);
                const resourceIds = !staff ? []
                    : service.tipService ? Array.from(new Set([...(service.resourceIds ?? []), staff.resourceId]))
                        : [staff.resourceId];
                await ServiceService.updateService({...service, status: service.status ?? notBookable, tipService: true, resourceIds});
            }
            await Promise.all([
                queryClient.invalidateQueries(['digitalSigningTipServices', sectorId]),
                queryClient.invalidateQueries('allServices'),
            ]);
            onClose();
        } catch (e: any) {
            const message = e?.response?.data?.message;
            setError(typeof message === 'string' && message.trim() ? message.trim() : t('Something went wrong'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => !saving && onClose()} maxWidth={'sm'} fullWidth fullScreen={fullScreen} aria-labelledby={TITLE_ID}>
            <DialogTitle component={'div'} sx={{pb: 1}}>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={2}>
                    <Typography component={'h2'} id={TITLE_ID} variant={'h6'} fontWeight={600} noWrap>
                        {staff ? t('Tip service for {{name}}', {name: staff.name}) : t('Default tip service')}
                    </Typography>
                    <IconButton onClick={onClose} size={'small'} aria-label={t('Close')} disabled={saving}>
                        <CloseIcon/>
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{px: 3, py: 3}}>
                <Stack gap={3}>
                    <Typography variant={'body2'} color={'text.secondary'}>
                        {staff
                            ? t('The tips of {{name}} are posted with this service.', {name: staff.name})
                            : t('The tip service without staff: it takes the tips of all staff who have no tip service of their own.')}
                    </Typography>

                    <NewStatusPicker<Mode>
                        value={mode}
                        onChange={setMode}
                        ariaLabel={t('Tip service')}
                        options={[
                            {value: 'CREATE', label: t('Create new')},
                            {value: 'LINK', label: t('Use existing'), disabled: !choices.length},
                        ]}
                    />

                    {mode === 'CREATE' ? (
                        <>
                            <DialogField label={`${t('Name')} *`} hint={t('The tip line\'s name on the folio.')}>
                                <TextField
                                    inputProps={{'aria-label': t('Name')}}
                                    size={'small'}
                                    value={name}
                                    onChange={event => setName(event.target.value)}
                                    onKeyDown={event => {
                                        if (event.key === 'Enter') void save();
                                    }}
                                    fullWidth
                                    autoFocus
                                />
                            </DialogField>
                            <DialogField label={t('Market Code (SKU)')} hint={t('The code the tip goes to the PMS with.')}>
                                <TextField
                                    inputProps={{'aria-label': t('Market Code (SKU)')}}
                                    size={'small'}
                                    value={marketCode}
                                    onChange={event => setMarketCode(event.target.value)}
                                    fullWidth
                                />
                            </DialogField>
                        </>
                    ) : (
                        <DialogField label={`${t('Service')} *`} hint={t('Services of the sector that guests can\'t book.')}>
                            <Stack gap={1}>
                                <Autocomplete
                                    options={choices}
                                    value={picked}
                                    onChange={(_event, service) => setPicked(service)}
                                    getOptionLabel={service => service.name || t('Service')}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderInput={params => <TextField {...params} size={'small'} inputProps={{...params.inputProps, 'aria-label': t('Service')}}/>}
                                    fullWidth
                                />
                                {replacesResources && (
                                    <Typography variant={'body2'} color={'text.secondary'}>
                                        {t('Its linked resources are replaced.')}
                                    </Typography>
                                )}
                            </Stack>
                        </DialogField>
                    )}

                    {!!error && <Alert severity={'error'}>{error}</Alert>}
                </Stack>
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2}}>
                <Button onClick={onClose} disabled={saving}>{t('Cancel')}</Button>
                <LoadingButton variant={'contained'} onClick={() => void save()} loading={saving} disabled={!canSave}>
                    {t('Save')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default DigitalSigningTipServiceDialog;
